import { useRef } from "react";
import { Box, Flex, Heading, Image, Text } from "@chakra-ui/react";
import {
  FaBullseye,
  FaChartLine,
  FaDatabase,
  FaFileImport,
  FaLayerGroup,
  FaPercent,
} from "react-icons/fa6";
import { useParallaxTilt, useReveal } from "../hooks/landingEffects";

// Illustrative figures for the public landing dashboard — chosen to convey
// "Spearbi tracks sales clearly" without exposing analytic outputs (forecasts,
// strike rate, days cover live in the authenticated app).
const KPIS = [
  {
    label: "Sales MTD",
    value: "R3.42M",
    delta: "▲ 4.2%",
    down: false,
    accent: "linear-gradient(180deg, #58b6c0, #04aec1)",
  },
  {
    label: "Sales YTD",
    value: "R8.21M",
    delta: "▲ 5.6%",
    down: false,
    accent: "linear-gradient(180deg, #818cf8, #6366f1)",
  },
  {
    label: "Active stores",
    value: "184",
    delta: "▲ 6",
    down: false,
    accent: "linear-gradient(180deg, #f472b6, #ec4899)",
  },
  {
    label: "SKUs ranged",
    value: "14",
    delta: "▲ 1",
    down: false,
    accent: "linear-gradient(180deg, #fbbf24, #f59e0b)",
  },
];

// Visual mock of the in-app sidebar — must remain non-interactive (Box, not Link)
// per design handoff README §5 and §8 of the MUST checklist.
const SIDEBAR_ITEMS = [
  { label: "Sales", Icon: FaChartLine, active: true },
  { label: "Strike Rate", Icon: FaBullseye },
  { label: "Ranging", Icon: FaLayerGroup },
  { label: "Margin", Icon: FaPercent },
  { label: "Imports", Icon: FaFileImport },
  { label: "EDI", Icon: FaDatabase },
];

const ProductPreview = () => {
  const ref = useRef(null);
  useReveal(ref);
  useParallaxTilt(ref);

  return (
    <Box
      ref={ref}
      id="product-preview"
      className="lp-reveal lp-product-preview"
      mt="56px"
      mx="auto"
      maxW="1140px"
      borderRadius="22px"
      scrollMarginTop="80px"
      border="1px solid rgba(255,255,255,0.10)"
      bg="linear-gradient(180deg, rgba(15,22,38,0.7), rgba(15,22,38,0.55))"
      backdropFilter="blur(14px)"
      boxShadow="0 60px 120px -40px rgba(99,102,241,0.45), 0 30px 70px -20px rgba(236,72,153,0.25), inset 0 1px 0 rgba(255,255,255,0.10)"
      overflow="hidden"
      position="relative"
      transformOrigin="50% 0%"
      _before={{
        content: '""',
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        height: "1px",
        background:
          "linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)",
      }}
    >
      <Flex
        align="center"
        gap="14px"
        px="16px"
        py="10px"
        borderBottom="1px solid rgba(255,255,255,0.06)"
        bg="rgba(0,0,0,0.18)"
      >
        <Flex gap="6px">
          <Box w="11px" h="11px" borderRadius="50%" bg="#ff5f57" />
          <Box w="11px" h="11px" borderRadius="50%" bg="#febc2e" />
          <Box w="11px" h="11px" borderRadius="50%" bg="#28c840" />
        </Flex>
        <Box
          flex="1"
          maxW="360px"
          mx="auto"
          fontSize="11px"
          color="rgba(230,236,245,0.55)"
          bg="rgba(255,255,255,0.04)"
          border="1px solid rgba(255,255,255,0.06)"
          px="12px"
          py="5px"
          borderRadius="9999px"
          textAlign="center"
          fontFamily="'Source Code Pro', Menlo, Monaco, Consolas, monospace"
        >
          spearbi.app/sales
        </Box>
        <Box w="50px" />
      </Flex>

      <Box
        display="grid"
        gridTemplateColumns={{ base: "1fr", lg: "200px 1fr" }}
        minH="460px"
      >
        <Box
          display={{ base: "none", lg: "block" }}
          bg="linear-gradient(160deg, rgba(88,182,192,0.92), rgba(4,174,193,0.92))"
          px="14px"
          py="18px"
          color="white"
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage: "url('/navbar_bg.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            opacity: 0.55,
            mixBlendMode: "screen",
          }}
        >
          <Box
            position="relative"
            zIndex={1}
            bg="rgba(255,255,255,0.14)"
            border="1px solid rgba(255,255,255,0.30)"
            borderRadius="12px"
            px="8px"
            py="10px"
            textAlign="center"
            backdropFilter="blur(8px)"
            mb="16px"
            boxShadow="inset 0 1px 0 rgba(255,255,255,0.4)"
          >
            <Image
              src="/spearbi_ai2.png"
              alt="Spear Management"
              h="56px"
              mx="auto"
              objectFit="contain"
            />
          </Box>

          <Flex direction="column" gap="5px" position="relative" zIndex={1}>
            {SIDEBAR_ITEMS.map((item) => (
              <Flex
                key={item.label}
                align="center"
                gap="9px"
                fontSize="11px"
                pl={item.active ? "7px" : "10px"}
                pr="10px"
                py="7px"
                borderRadius="10px"
                bg={item.active ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.06)"}
                border="1px solid rgba(255,255,255,0.12)"
                borderLeft={item.active ? "3px solid #ec4899" : undefined}
                color="white"
                fontWeight={500}
                backdropFilter="blur(8px)"
                boxShadow={item.active ? "inset 0 1px 0 rgba(255,255,255,0.4)" : undefined}
              >
                <Box w="12px" fontSize="10px" display="inline-flex">
                  <item.Icon />
                </Box>
                {item.label}
              </Flex>
            ))}
          </Flex>
        </Box>

        <Box
          px="22px"
          py="18px"
          bg="linear-gradient(180deg, rgba(247,250,252,0.98), white)"
          color="#2d3748"
        >
          <Flex justify="space-between" align="center" mb="16px">
            <Heading
              as="h3"
              fontSize="16px"
              fontWeight={700}
              m={0}
              letterSpacing="-0.01em"
              color="#2d3748"
            >
              Sales · Demo Company at Pick n Pay
            </Heading>
            <Box
              bg="linear-gradient(135deg, #04aec1, #6366f1)"
              color="white"
              fontSize="10px"
              fontWeight={600}
              px="10px"
              py="4px"
              borderRadius="9999px"
            >
              Live · MTD
            </Box>
          </Flex>

          <Box
            display="grid"
            gridTemplateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }}
            gap="10px"
            mb="14px"
          >
            {KPIS.map((kpi) => (
              <Box
                key={kpi.label}
                bg="white"
                border="1px solid #e2e8f0"
                borderRadius="10px"
                p="12px"
                position="relative"
                overflow="hidden"
                boxShadow="0 1px 3px rgba(0,0,0,0.04)"
                _before={{
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "3px",
                  background: kpi.accent,
                }}
              >
                <Text
                  fontSize="9px"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                  color="#718096"
                  fontWeight={600}
                >
                  {kpi.label}
                </Text>
                <Text
                  fontSize="18px"
                  fontWeight={700}
                  letterSpacing="-0.01em"
                  mt="2px"
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {kpi.value}
                </Text>
                <Text
                  fontSize="9px"
                  color={kpi.down ? "#db2777" : "#0d9488"}
                  fontWeight={700}
                  mt="2px"
                >
                  {kpi.delta}
                </Text>
              </Box>
            ))}
          </Box>

          <Box
            bg="white"
            border="1px solid #e2e8f0"
            borderRadius="10px"
            pt="12px"
            pr="14px"
            pb="8px"
            pl="14px"
            h="200px"
            position="relative"
            overflow="hidden"
            _before={{
              content: '""',
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              height: "2px",
              background: "linear-gradient(90deg, #04aec1, #6366f1, #ec4899)",
            }}
          >
            <Flex justify="space-between" align="center" mb="6px">
              <Heading as="h4" fontSize="12px" fontWeight={700} m={0}>
                Daily Sales — May 2026
              </Heading>
              <Flex fontSize="9px" color="#718096" gap="10px">
                <Box as="span">
                  <Box
                    as="span"
                    display="inline-block"
                    w="7px"
                    h="7px"
                    borderRadius="50%"
                    bg="#04aec1"
                    verticalAlign="middle"
                    mr="4px"
                  />
                  This year
                </Box>
                <Box as="span">
                  <Box
                    as="span"
                    display="inline-block"
                    w="7px"
                    h="7px"
                    borderRadius="50%"
                    bg="#a0aec0"
                    verticalAlign="middle"
                    mr="4px"
                  />
                  Last year
                </Box>
              </Flex>
            </Flex>
            <Box as="svg" viewBox="0 0 600 140" preserveAspectRatio="none" w="100%" h="140px">
              <defs>
                <linearGradient id="lpAreaCur" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#04aec1" stopOpacity="0.32" />
                  <stop offset="100%" stopColor="#04aec1" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="lpStroke" x1="0" x2="1" y1="0" y2="0">
                  <stop offset="0%" stopColor="#04aec1" />
                  <stop offset="60%" stopColor="#6366f1" />
                  <stop offset="100%" stopColor="#ec4899" />
                </linearGradient>
              </defs>
              <path
                fill="url(#lpAreaCur)"
                d="M0,110 L40,100 L80,95 L120,80 L160,75 L200,82 L240,68 L280,60 L320,52 L360,40 L400,46 L440,32 L480,22 L520,18 L560,22 L600,12 L600,140 L0,140 Z"
              />
              <path
                fill="none"
                stroke="url(#lpStroke)"
                strokeWidth="2.5"
                d="M0,110 L40,100 L80,95 L120,80 L160,75 L200,82 L240,68 L280,60 L320,52 L360,40 L400,46 L440,32 L480,22 L520,18 L560,22 L600,12"
              />
              <path
                fill="none"
                stroke="#a0aec0"
                strokeWidth="1.5"
                strokeDasharray="3 3"
                opacity="0.6"
                d="M0,118 L40,112 L80,118 L120,108 L160,100 L200,108 L240,96 L280,92 L320,84 L360,76 L400,82 L440,72 L480,62 L520,58 L560,52 L600,58"
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductPreview;
